import { CalculationGroupResponse, CalculationRuleSummandInput } from 'entities/tag';
import { CalcPickDraft } from 'app/state/contexts/CalcPickContext';

export interface PickParent {
  conceptId: string;
  conceptName: string;
  tagId: string;
}

/**
 * F4b — pure calc-pick helpers extracted from TagInfo.
 *
 * These were inline closures in TagInfo (enterPickMode's draft build, savePickMode's
 * summand map, the term-highlight effect); pulling them out makes the flow unit-testable
 * and keeps the component render-focused. All behavior is preserved verbatim.
 */
export function buildCalcPickDraftFromGroup(group: CalculationGroupResponse | null, parent: PickParent): CalcPickDraft {
  const summandsByConcept = new Map<string, CalculationRuleSummandInput>();
  const termConceptIds = new Set<string>();
  const pickedTagIds = new Set<string>();
  for (const row of group?.rows ?? []) {
    if (row.isParent || !row.conceptId) continue;
    termConceptIds.add(row.conceptId);
    if (row.tagId) pickedTagIds.add(row.tagId);
    summandsByConcept.set(row.conceptId, {
      summandConceptId: row.conceptId,
      weight: row.weight,
      ...(row.dimQualifiers && row.dimQualifiers.length > 0
        ? { dimQualifiers: row.dimQualifiers.map((q) => ({ axisConceptId: q.axisConceptId, memberConceptId: q.memberConceptId })) }
        : {}),
    });
  }
  return {
    ruleId: group?.ruleId ?? null,
    parentConceptName: parent.conceptName,
    parentConceptId: parent.conceptId,
    parentTagId: parent.tagId,
    termConceptIds,
    pickedTagIds,
    summandsByConcept,
  };
}

export function draftToSummands(draft: CalcPickDraft): CalculationRuleSummandInput[] {
  return [...draft.termConceptIds].map((conceptId) => draft.summandsByConcept.get(conceptId) ?? { summandConceptId: conceptId });
}

/** Tag ids of the summand rows in the group whose parent is the active concept — used to highlight the terms. */
export function computeTermHighlightIds(groups: CalculationGroupResponse[] | null | undefined, activeConceptId: string | undefined): Set<string> {
  const ids = new Set<string>();
  if (!activeConceptId || !groups) return ids;
  for (const group of groups) {
    const parentRow = group.rows.find((r) => r.isParent);
    if (!parentRow?.conceptId || parentRow.conceptId !== activeConceptId) continue;
    for (const row of group.rows) {
      if (!row.isParent && row.tagId) ids.add(row.tagId);
    }
  }
  return ids;
}
